import { type DuaPartType } from '@components/DuaPage/DuaPageComponent'
import Tooltip from '@components/shared/Tooltip/Tooltip'
import { useUserData } from '@contexts/UserDataContext'
import useFontSizes from '@hooks/useFontSizes'
import DuaPartBookmark from './DuaPartBookmark'

/** Renders grouped bâb cards while preserving bookmarks and verse translation tooltips. */
export function CevsenCompact({
  data,
  page,
}: {
  data: { [key: string]: DuaPartType[] }
  page: number
}) {
  const { userData } = useUserData()
  const { duaArabFontSize, duaMealFontSize } = useFontSizes()
  const parts = Object.keys(data)

  return (
    <div className="space-y-3">
      {parts?.map((part) => {
        return (
          <article
            key={part}
            id={`bolum-${part}`}
            className="rounded-2xl border border-slate-200 bg-white px-3 py-4 shadow-sm sm:p-6 dark:border-[#1e3445]/60 dark:bg-[#13222e]"
          >
            <div className="mb-3 flex items-center justify-between">
              <h2 className="rounded-lg border border-teal-200 bg-teal-50 px-3 py-1 text-sm font-bold text-teal-600 dark:border-teal-500/20 dark:bg-[#0c3947] dark:text-teal-400">
                {part}. Bâb
              </h2>
              <DuaPartBookmark type="cevsen" page={page} partKey={Number(part)} />
            </div>
            {userData.duaArabicVisibility === 'show' && (
              <div className="my-3 flex flex-row-reverse flex-wrap text-slate-900 dark:text-white">
                {data[part].map((item, i) => {
                  return (
                    <Tooltip trigger="click" content={item.meal} key={i}>
                      <div
                        className={`dua-arabic ml-2 mr-2 flex items-center whitespace-pre-wrap text-right hover:text-green-600 dark:hover:text-green-400 ${userData.arabicFontFamily}`}
                        key={i}
                      >
                        {item.arab}
                        {i !== data[part].length - 1 && <>●</>}
                      </div>
                    </Tooltip>
                  )
                })}
              </div>
            )}
            {userData.duaMealVisibility === 'show' && (
              <div className="py-2">
                {data[part].map((item, i) => {
                  return (
                    <span className="dua-meal tracking-wide" key={i}>
                      {i + 1}) {item.meal}{' '}
                    </span>
                  )
                })}
              </div>
            )}
            {userData.duaLatinVisibility === 'show' && (
              <div className="py-2">
                {data[part].map((item, i) => {
                  return (
                    <span className="dua-meal tracking-wide" key={i}>
                      {i + 1}) {item.latin}{' '}
                    </span>
                  )
                })}
              </div>
            )}
          </article>
        )
      })}
      <style jsx global>
        {`
          .dua-arabic {
            font-size: ${duaArabFontSize}px;
            line-height: 1.8;
            direction: rtl;
          }

          @media (min-width: 600px) {
            .dua-arabic {
              text-align: justify;
            }
          }

          .dua-meal {
            font-size: ${duaMealFontSize}px;
            direction: ltr;
          }
        `}
      </style>
    </div>
  )
}
